import { useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "../../context/react-auth0-spa";
import { v4 as uuidv4 } from 'uuid';

const API_BASE_URL = process.env.GATSBY_NEWA_API_ORIGIN;
const NEWA_TRACKING_URL = API_BASE_URL ? `${API_BASE_URL}/tracking` : null;

// TODO: Generate these types from newa-api openapi.json
export enum TrackingEventType {
  SESSION_START = 'session_start',
  SESSION_END = 'session_end',
  MODEL_VIEWED = 'model_viewed',
  PARAMETER_CHANGED = 'parameter_changed',
  DATA_EXPORTED = 'data_exported',
  ERROR_OCCURRED = 'error_occurred'
}

export interface TrackingEventData {
  parameterName?: string;
  parameterValue?: string;
  previousValue?: string;
  additionalContext?: Record<string, string>;
}

export interface TrackingConfig {
  modelId: string;
  modelType?: string;
  stationId?: string;
}

export const useTracking = (config: TrackingConfig) => {
  const { user, isAuthenticated } = useAuth0();
  const sessionIdRef = useRef<string>(uuidv4());

  // Fire and forget tracking
  const sendTrackingEvent = useCallback(async (
    eventType: TrackingEventType,
    eventData?: TrackingEventData
  ) => {
    if (!NEWA_TRACKING_URL || !config.modelId) return;

    try {
      await fetch(NEWA_TRACKING_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          eventType,
          modelId: config.modelId.toLowerCase().replace(/[\s.]/g, '-'),
          stationId: config.stationId,
          userId: isAuthenticated ? user?.name : undefined,
          eventData,
        }),
      });
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  }, [config, isAuthenticated, user?.name]);

  // Track session lifecycle
  useEffect(() => {
    if (!NEWA_TRACKING_URL || !config.modelId) return;

    // Session start
    sendTrackingEvent(TrackingEventType.SESSION_START);

    // Model viewed after session start
    setTimeout(() => {
      sendTrackingEvent(TrackingEventType.MODEL_VIEWED);
    }, 100);

    // Session end on unmount
    return () => {
      const data = JSON.stringify({
        sessionId: sessionIdRef.current,
        eventType: TrackingEventType.SESSION_END,
        modelId: config.modelId.toLowerCase().replace(/\s/g, '-'),
        modelType: config.modelType,
        stationId: config.stationId,
        userId: isAuthenticated ? user?.name : undefined,
      });
      navigator.sendBeacon(NEWA_TRACKING_URL, data);
    };
  }, [config.modelId]); // Only re-run if modelId changes

  return { trackEvent: sendTrackingEvent };
};
